<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { LANG_ENGLISH, LANG_FRENCH, VOUCHER, VOUCHER_CONTENT } from '@/constants';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Toast from 'primevue/toast';
import HTMLEditor from '@/components/common/HTMLEditor.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const { canEditConfigurableItem } = usePromoComposable();
const configurationStore = useItemConfigurationStore();
const previewUrlEn = ref();
const previewUrlFr = ref();

const status = computed(() => configurationStore.selectStatus);

const voucherConfigs = computed(() => configurationStore.selectVoucherConfig);

const handleVoucherSubmit = async (type: string) => {
  await configurationStore.updateVoucher(type, voucherConfigs);
  if (status.value === true) {
    toast.add({ severity: 'success', summary: '', detail: t('notification.update_success'), life: 3000 });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  }
};

const handlePreviewVoucherContent = async (lang: string) => {
  const payload = {
    lang: lang,
    content: ''
  };
  if (lang === LANG_ENGLISH) {
    previewUrlEn.value = null;
    payload.content = voucherConfigs.value.voucherContent?.templateEn;
    configurationStore.previewVoucherContent(payload).then((res) => {
      previewUrlEn.value = res + '#toolbar=0';
    });
  } else {
    previewUrlFr.value = null;
    payload.content = voucherConfigs.value.voucherContent.templateFr;
    configurationStore.previewVoucherContent(payload).then((res) => {
      previewUrlFr.value = res + '#toolbar=0';
    });
  }
};

onBeforeMount(() => {
  configurationStore.getVoucher(VOUCHER);
  configurationStore.getVoucher(VOUCHER_CONTENT);
});
</script>

<template>
  <Toast position="top-left" group="tl" />
  <Splitter>
    <SplitterPanel>
      <Splitter layout="vertical">
        <SplitterPanel class="p-5">
          <h5>{{ $t('voucher.config.email_content') }}</h5>
          <div class="grid">
            <div class="flex flex-col col-6 email-content-en">
              <label for="email-content-en" class="font-semibold">{{ $t('promos.section_title_english') }}</label>
              <HTMLEditor :readonly="!canEditConfigurableItem" v-model="voucherConfigs.emailContent.templateEn" classEditor="custom-editor" class="custom-editor mb-1" :placeholder="$t('common.intro')" />
            </div>
            <div class="flex flex-col col-6 email-content-fr">
              <label for="email-content-fr" class="font-semibold">{{ $t('promos.section_title_french') }}</label>
              <HTMLEditor id="email-content-fr" :readonly="!canEditConfigurableItem" v-model="voucherConfigs.emailContent.templateFr" class="custom-editor mb-1" :placeholder="$t('common.intro')" />
            </div>
          </div>
          <br />
          <div class="flex flex-wrap gap-3 justify-content-center">
            <Button id="email-submit-btn" v-if="canEditConfigurableItem" :label="$t('promos.save')" icon="pi pi-check" class="p-component cus-btn" @click="handleVoucherSubmit(VOUCHER)" />
          </div>
        </SplitterPanel>
        <SplitterPanel class="p-5">
          <h5>{{ $t('voucher.config.voucher_content') }}</h5>
          <div class="grid voucher-content-en">
            <div class="flex flex-col col-6">
              <label for="voucher-content-en" class="font-semibold">{{ $t('promos.section_title_english') }}</label>
              <HTMLEditor id="voucher-content-en" :readonly="!canEditConfigurableItem" v-model="voucherConfigs.voucherContent.templateEn" class="custom-editor mb-1" :placeholder="$t('common.intro')" />
            </div>
            <div class="flex flex-col col-6">
              <span class="font-semibold">{{ $t('voucher.config.preview') }}</span>
              <iframe title="Preview" :src="previewUrlEn" allowfullscreen="true" class="h-full" />
            </div>
          </div>
          <Button id="voucher-en-preview" :label="$t('voucher.config.preview')" icon="pi pi-eye" severity="info" class="cus-btn" @click="handlePreviewVoucherContent(LANG_ENGLISH)" />
          <Divider />
          <div class="grid voucher-content-fr">
            <div class="flex flex-col col-6">
              <label for="voucher-content-fr" class="font-semibold">{{ $t('promos.section_title_french') }}</label>
              <HTMLEditor id="voucher-content-fr" :readonly="!canEditConfigurableItem" v-model="voucherConfigs.voucherContent.templateFr" class="custom-editor mb-1" :placeholder="$t('common.intro')" />
            </div>
            <div class="flex flex-col col-6">
              <span class="font-semibold">{{ $t('voucher.config.preview') }}</span>
              <iframe title="Preview" :src="previewUrlFr" class="h-full">{{ $t('notification.not_support_iframe') }}</iframe>
            </div>
          </div>
          <br />
          <div class="grid">
            <div class="col-2">
              <Button id="voucher-preview-btn" :label="$t('voucher.config.preview')" icon="pi pi-eye" severity="info" class="cus-btn" @click="handlePreviewVoucherContent(LANG_FRENCH)" />
            </div>
            <div class="col-offset-10"></div>
          </div>
          <div class="grid">
            <div class="col-2 flex justify-content-center w-full">
              <Button id="voucher-save-btn" v-if="canEditConfigurableItem" :label="$t('promos.save')" icon="pi pi-check" class="cus-btn" @click="handleVoucherSubmit(VOUCHER_CONTENT)" />
            </div>
            <div class="col-offset-10"></div>
          </div>
        </SplitterPanel>
      </Splitter>
    </SplitterPanel>
  </Splitter>
</template>
